import { GraphQLError } from 'graphql';
import { maskPersonalIdentityNumbers } from 'utils/mask-personal-identity-numbers';

/**
 * DD-3780: the last few GraphQL and network errors, kept in memory only, so the help
 * assistant can answer "why did that fail?". Never variables or response data: they can
 * hold patient data. Messages are masked, quoted values dropped and the length capped.
 */
export type RecentError = {
    operation: string;
    message: string;
    code?: string;
    requestId?: string;
    /** ISO timestamp. */
    at: string;
};

const MAX_ERRORS = 5;
const MAX_MESSAGE_LENGTH = 300;
/** Older errors are not what "I just got an error" means. */
const MAX_AGE_MS = 30 * 60 * 1000;
/** The chat's own calls: an error there is shown in the chat already. */
const IGNORED_OPERATIONS = new Set(['assistantChat', 'submitSupportTicket']);

let buffer: RecentError[] = [];
const listeners = new Set<(error: RecentError) => void>();

/** Quoted text in an error message is usually an input value (a name, a search), never needed. */
const withoutQuotedValues = (text: string): string => text.replace(/"[^"]*"|'[^']*'|“[^”]*”/g, '"…"');

const cleanMessage = (message: unknown): string =>
    maskPersonalIdentityNumbers(withoutQuotedValues(String(message ?? '')))
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, MAX_MESSAGE_LENGTH);

const cleanId = (value: unknown): string | undefined =>
    typeof value === 'string' && /^[\w.:-]{1,100}$/.test(value) ? value : undefined;

const push = (error: RecentError) => {
    if (IGNORED_OPERATIONS.has(error.operation)) return;
    buffer = [...buffer, error].slice(-MAX_ERRORS);
    listeners.forEach((listener) => listener(error));
};

/** DD-3780: notified of each new error (the help button offers the assistant). Returns an unsubscribe. */
export const onRecentError = (listener: (error: RecentError) => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

type HeadersLike = { get?: (name: string) => string | null } | undefined;

const requestIdFrom = (headers: HeadersLike): string | undefined =>
    cleanId(headers?.get?.('x-request-id') ?? headers?.get?.('x-trace-id') ?? undefined);

/** The code the UI would act on: AppError's type (e.g. UNAUTHORIZED), else Apollo's code. */
const codeOf = (error: GraphQLError): string | undefined => {
    const extensions = (error?.extensions ?? {}) as { code?: unknown; exception?: { type?: unknown } };
    return cleanId(extensions.exception?.type ?? extensions.code);
};

export const recordGraphQLErrors = (
    operation: string | undefined,
    errors: readonly GraphQLError[],
    headers?: HeadersLike,
    now: Date = new Date(),
): void => {
    errors.forEach((error) => {
        const extensions = (error?.extensions ?? {}) as Record<string, unknown>;
        const code = codeOf(error);
        push({
            operation: operation || 'unknown',
            // Bad input messages quote the rejected variable value: keep only the code.
            message: code === 'BAD_USER_INPUT' ? 'Invalid input' : cleanMessage(error?.message),
            code,
            requestId: cleanId(extensions.requestId ?? extensions.traceId) ?? requestIdFrom(headers),
            at: now.toISOString(),
        });
    });
};

export const recordNetworkError = (
    operation: string | undefined,
    error: { message?: string; statusCode?: number; response?: { headers?: HeadersLike } },
    headers?: HeadersLike,
    now: Date = new Date(),
): void => {
    if ((error as Error)?.name === 'AbortError') return;
    push({
        operation: operation || 'unknown',
        message: cleanMessage(error?.message),
        code: error?.statusCode ? `HTTP_${error.statusCode}` : 'NETWORK_ERROR',
        requestId: requestIdFrom(error?.response?.headers ?? headers),
        at: now.toISOString(),
    });
};

/** DD-3780: a red-screen crash from just before this load, as a recent error for the assistant. */
export const recordCrash = (crash: { message: string; page: string; at: string; errorReportId?: string }): void => {
    push({
        operation: `page crash on ${crash.page}`.slice(0, 120),
        message: cleanMessage(crash.message),
        code: 'PAGE_CRASH',
        requestId: cleanId(crash.errorReportId),
        at: crash.at,
    });
};

/** Errors from the last half hour, oldest first. */
export const recentErrors = (now: Date = new Date()): RecentError[] =>
    buffer.filter((error) => now.getTime() - new Date(error.at).getTime() <= MAX_AGE_MS);

export const clearRecentErrors = (): void => {
    buffer = [];
};
